import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Socket } from 'socket.io-client';
import ExamModeSegment from '../examModeSegment';
import ChromaticPresetPanel from '../chromaticPreset';
import { ChromaticPresetWarnings } from '../chromaticPreset/ChromaticPresetWarnings';
import OvalAdjustGlyph from '../../atoms/ovalAdjustGlyph';
import OvalRefractionPanel from '../ovalRefractionPanel';
import DoctorTestModalDyopPreview from '../doctorTestModalDyopPreview';
import dyopsAction from '../../../redux/actions/dyops';
import { mapping } from '../../../config/mapping';
import type { dyopsType, ExamMode } from '../../../types/dyops';
import { applyExamModeToDyops } from '../../../tools/applyOvalDyops';
import { flushScheduledPersistDoctorOvalPreference } from '../../../tools/persistDoctorOvalPreference';
import { dyopPreviewSpinDurationSec, patientTestDyopDiameterPx } from '../../../tools/dyopPreviewSpin';
import { useWindowSize } from '../../../tools';
import { stateType } from '../../../types/types';
import strings from '../../../localization';
import './index.scss';

export type PatientSelfTestMonitorControlsProps = {
  dyops: dyopsType;
  socketRef: React.MutableRefObject<Socket | undefined>;
  screenRatio?: number;
  disabled?: boolean;
  isInfantSession?: boolean;
  isIntegration?: boolean;
  inputType?: string;
};

// Renders the sun glyph for the chromatic scheme trigger button.
const ChromaticSchemeGlyph: FC = () => (
  <svg
    className="doctor-test__chromatic-modal-icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="3.5" />
    <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" />
  </svg>
);

/** Self-test PC monitor: Colors, VA/Oval, and oval modal (doctor-equivalent chrome). */
const PatientSelfTestMonitorControls: FC<PatientSelfTestMonitorControlsProps> = ({
  dyops,
  socketRef,
  screenRatio,
  disabled,
  isInfantSession = false,
  isIntegration = false,
  inputType = 'selfTest',
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state: stateType) => state.user.user);
  const appData = useSelector((state: stateType) => state.app.app);
  const [windowWidth] = useWindowSize();
  const [chromaticModalOpen, setChromaticModalOpen] = useState(false);
  const [ovalModalOpen, setOvalModalOpen] = useState(false);
  const [chromaticModalWarnings, setChromaticModalWarnings] = useState({
    contrastLow: false,
    customInvalid: false,
  });
  const [chromaticPreviewDyops, setChromaticPreviewDyops] = useState<dyopsType | null>(null);
  const [ovalPreviewDyops, setOvalPreviewDyops] = useState<dyopsType | null>(null);
  const chromaticModalWasOpen = useRef(false);
  const ovalModalWasOpen = useRef(false);
  const examModalPortalRef = useRef<HTMLDivElement>(null);

  const ratio = screenRatio ?? dyops.result.ratio ?? appData.ratio;

  const commitDyops = useCallback(
    (next: dyopsType) => {
      dispatch(dyopsAction.getDyops(next));
      socketRef.current?.emit('emitUpdateDoctor', { dyops: next });
      socketRef.current?.emit(
        'emitUpdatePatient',
        mapping.buildPatientInfo(
          {
            ratio: appData.ratio,
            distance: appData.distance,
            unit: appData.unit,
            footSteps: appData.footSteps,
            inputType,
            testType: next.result?.testType,
            examMode: next.examMode,
            user,
          },
          isIntegration,
        ),
      );
    },
    [
      appData.distance,
      appData.footSteps,
      appData.ratio,
      appData.unit,
      dispatch,
      inputType,
      isIntegration,
      socketRef,
      user,
    ],
  );

  const modalDyopPreview = useMemo(() => {
    const ringDiameter = windowWidth <= 1100 ? 53 : 66;
    const patientDiameter = patientTestDyopDiameterPx(
      dyops,
      ratio || 1,
      String(dyops.result.distance || appData.distance),
      String(dyops.result.unit || appData.unit),
    );
    return {
      ringDiameter,
      spinDurationSec: dyopPreviewSpinDurationSec(ringDiameter, patientDiameter),
      rotation: dyops.rotation ? '' : 'counter-clockwise',
    };
  }, [windowWidth, dyops, ratio, appData.distance, appData.unit]);

  useEffect(() => {
    if (chromaticModalOpen && !chromaticModalWasOpen.current) {
      setChromaticPreviewDyops(dyops);
    }
    chromaticModalWasOpen.current = chromaticModalOpen;
  }, [chromaticModalOpen, dyops]);

  useEffect(() => {
    if (ovalModalOpen && !ovalModalWasOpen.current) {
      setOvalPreviewDyops(dyops);
    }
    ovalModalWasOpen.current = ovalModalOpen;
  }, [ovalModalOpen, dyops]);

  const handleChromaticWarningsChange = useCallback((state: { contrastLow: boolean; customInvalid: boolean }) => {
    setChromaticModalWarnings(state);
  }, []);

  useEffect(() => {
    if (!chromaticModalOpen) {
      setChromaticModalWarnings({ contrastLow: false, customInvalid: false });
      setChromaticPreviewDyops(null);
    }
  }, [chromaticModalOpen]);

  const handleChromaticPreviewDyops = useCallback((next: dyopsType) => {
    setChromaticPreviewDyops(next);
  }, []);

  useEffect(() => {
    if (!ovalModalOpen) {
      setOvalPreviewDyops(null);
    }
  }, [ovalModalOpen]);

  const handleOvalPreviewDyops = useCallback((next: dyopsType) => {
    setOvalPreviewDyops(next);
  }, []);

  const handleExamModeChange = useCallback(
    (mode: ExamMode) => {
      if (mode === 'va') {
        flushScheduledPersistDoctorOvalPreference();
      }
      const withDistance = {
        ...dyops,
        result: {
          ...dyops.result,
          distance: dyops.result.distance || appData.distance,
          unit: dyops.result.unit || appData.unit,
          ratio,
        },
      };
      const next = applyExamModeToDyops(withDistance, mode, user, ratio);
      commitDyops(next);
    },
    [appData.distance, appData.unit, commitDyops, dyops, ratio, user],
  );

  const handleChromaticCommit = useCallback(
    (next: dyopsType) => {
      setChromaticPreviewDyops(next);
      commitDyops(next);
    },
    [commitDyops],
  );

  const handleOvalCommit = useCallback(
    (next: dyopsType) => {
      setOvalPreviewDyops(next);
      commitDyops(next);
    },
    [commitDyops],
  );

  // Portals exam modals into the exam root so fixed overlays match rotated mobile layout.
  const getExamModalPortalTarget = () => examModalPortalRef.current ?? document.body;

  return (
    <div className="patient-self-test-monitor-controls doctor-test" aria-hidden={false}>
      {ovalModalOpen
        ? createPortal(
            <>
              <div
                className="doctor-test__chromatic-modal-backdrop"
                role="presentation"
                onClick={() => setOvalModalOpen(false)}
              />
              <div className="doctor-test__chromatic-modal doctor-test__chromatic-modal--with-preview" role="dialog">
                <div className="doctor-test__chromatic-modal-head">
                  <span className="doctor-test__chromatic-modal-title">{strings.oval.heading}</span>
                  <button
                    type="button"
                    className="doctor-test__chromatic-modal-dismiss"
                    aria-label={strings.oval.closePanel}
                    onClick={() => setOvalModalOpen(false)}
                  >
                    <svg
                      className="doctor-test__chromatic-modal-dismiss-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="doctor-test__chromatic-modal-main">
                  <div className="doctor-test__chromatic-modal-split">
                    <div className="doctor-test__chromatic-modal-controls">
                      <OvalRefractionPanel
                        dyops={dyops}
                        screenRatio={ratio}
                        disabled={!!disabled}
                        onPreviewDyopsChange={handleOvalPreviewDyops}
                        onCommit={handleOvalCommit}
                      />
                    </div>
                    <div className="doctor-test__chromatic-modal-preview">
                      <DoctorTestModalDyopPreview
                        dyops={ovalPreviewDyops ?? dyops}
                        ringDiameter={modalDyopPreview.ringDiameter}
                        spinDurationSec={modalDyopPreview.spinDurationSec}
                        rotation={modalDyopPreview.rotation}
                      />
                    </div>
                  </div>
                  <div className="doctor-test__chromatic-modal-foot">
                    <div className="doctor-test__chromatic-modal-foot-inner doctor-test__chromatic-modal-foot-inner--start">
                      <p className="doctor-test__chromatic-modal-foot-slot">{strings.oval.readoutFooterLine1}</p>
                      <p className="doctor-test__chromatic-modal-foot-slot">{strings.oval.readoutFooterLine2}</p>
                    </div>
                  </div>
                </div>
              </div>
            </>,
            getExamModalPortalTarget(),
          )
        : null}
      {chromaticModalOpen
        ? createPortal(
            <>
              <div
                className="doctor-test__chromatic-modal-backdrop"
                role="presentation"
                onClick={() => setChromaticModalOpen(false)}
              />
              <div
                className="doctor-test__chromatic-modal doctor-test__chromatic-modal--with-preview"
                role="dialog"
                aria-labelledby="self-test-chromatic-modal-title"
              >
                <div className="doctor-test__chromatic-modal-head">
                  <span id="self-test-chromatic-modal-title" className="doctor-test__chromatic-modal-title">
                    {strings.chromatic.heading}
                  </span>
                  <button
                    type="button"
                    className="doctor-test__chromatic-modal-dismiss"
                    aria-label={strings.chromatic.closePanel}
                    onClick={() => setChromaticModalOpen(false)}
                  >
                    <svg
                      className="doctor-test__chromatic-modal-dismiss-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="doctor-test__chromatic-modal-main">
                  <div className="doctor-test__chromatic-modal-split">
                    <div className="doctor-test__chromatic-modal-controls">
                      <ChromaticPresetPanel
                        dyops={dyops}
                        disabled={!!disabled}
                        onCommit={handleChromaticCommit}
                        compact
                        variant="embedded"
                        onChromaticWarningsChange={handleChromaticWarningsChange}
                        onPreviewDyopsChange={handleChromaticPreviewDyops}
                      />
                    </div>
                    <div className="doctor-test__chromatic-modal-preview">
                      <DoctorTestModalDyopPreview
                        dyops={chromaticPreviewDyops ?? dyops}
                        forceCircular
                        ringDiameter={modalDyopPreview.ringDiameter}
                        spinDurationSec={modalDyopPreview.spinDurationSec}
                        rotation={modalDyopPreview.rotation}
                      />
                    </div>
                  </div>
                  <div className="doctor-test__chromatic-modal-foot">
                    <ChromaticPresetWarnings
                      contrastLow={chromaticModalWarnings.contrastLow}
                      customInvalid={chromaticModalWarnings.customInvalid}
                    />
                  </div>
                </div>
              </div>
            </>,
            getExamModalPortalTarget(),
          )
        : null}
      <div className="doctor-test__chromatic-trigger">
        <div className="doctor-test__chromatic-trigger-stack">
          <button
            type="button"
            className="doctor-test__chromatic-modal-btn btn btn_submit"
            disabled={disabled}
            onClick={() => setChromaticModalOpen(true)}
          >
            <span className="doctor-test__chromatic-modal-btn-inner">
              <ChromaticSchemeGlyph />
              <span className="doctor-test__chromatic-modal-btn-label">{strings.chromatic.openColorsModal}</span>
            </span>
          </button>
          {!isInfantSession ? (
            <ExamModeSegment value={dyops.examMode ?? 'va'} disabled={disabled} onChange={handleExamModeChange} />
          ) : null}
          {!isInfantSession && dyops.examMode === 'oval' ? (
            <button
              type="button"
              className="doctor-test__chromatic-modal-btn btn btn_submit"
              disabled={disabled}
              onClick={() => setOvalModalOpen(true)}
            >
              <span className="doctor-test__chromatic-modal-btn-inner">
                <OvalAdjustGlyph className="doctor-test__chromatic-modal-icon" />
                <span className="doctor-test__chromatic-modal-btn-label">{strings.oval.openPanel}</span>
              </span>
            </button>
          ) : null}
        </div>
      </div>
      <div ref={examModalPortalRef} className="doctor-test__exam-modal-portal" aria-hidden />
    </div>
  );
};

export default PatientSelfTestMonitorControls;
